import React, { useEffect, useState } from "react";
import { Clock, User } from "lucide-react";
import useApi from "../../hooks/useApi";

const TimelineTab = ({
  BASE_URL,
  COMPANY_CODE,
  DEPARTMENT,
  modificationType,
  invoiceId,
}) => {
  const { request, loading } = useApi();
  const [modificationLogs, setModificationLogs] = useState([]);

  const getIconAndColors = (type) => {
    switch (type) {
      case "created":
        return {
          bgColor: "bg-green-50",
          iconColor: "text-green-600",
          borderColor: "border-green-200",
          statusBg: "bg-green-100",
          statusText: "text-green-700",
          lineColor: "bg-green-300",
        };
      case "warning":
        return {
          bgColor: "bg-yellow-50",
          iconColor: "text-yellow-600",
          borderColor: "border-yellow-200",
          statusBg: "bg-yellow-100",
          statusText: "text-yellow-700",
          lineColor: "bg-yellow-300",
        };
      case "error":
        return {
          bgColor: "bg-red-50",
          iconColor: "text-red-600",
          borderColor: "border-red-200",
          statusBg: "bg-red-100",
          statusText: "text-red-700",
          lineColor: "bg-red-300",
        };
      default:
        return {
          bgColor: "bg-blue-50",
          iconColor: "text-blue-600",
          borderColor: "border-blue-200",
          statusBg: "bg-blue-100",
          statusText: "text-blue-700",
          lineColor: "bg-blue-300",
        };
    }
  };

  const fetchLogs = async (type) => {
    const url = `${BASE_URL}/api/CRMTaskHistoryList/${COMPANY_CODE}/${type}/${DEPARTMENT}/${invoiceId}`;
    try {
      const response = await request({ url, method: "GET" });
      return response || [];
    } catch (err) {
      console.error(`Failed to fetch ${type} logs:`, err);
      return [];
    }
  };

  useEffect(() => {
    const loadLogs = async () => {
      const [mods] = await Promise.all([fetchLogs(modificationType)]);
      setModificationLogs(mods);
    };
    if (invoiceId) loadLogs();
  }, [invoiceId]);

  // Format date and time from create_date
  const formatDateTime = (isoString) => {
    if (!isoString) return { date: "-", time: "-" };
    const date = new Date(isoString);
    const formattedDate = date.toLocaleDateString("en-GB"); // DD/MM/YYYY
    const formattedTime = date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });
    return { date: formattedDate, time: formattedTime };
  };

  return (
    <div className="lg:w-[calc(90vw-5vw)] w-[calc(100vw-6vw)] overflow-x-auto">
      <div className="mb-4">
        <h2 className="text-xl font-semibold text-gray-800 mb-1">
          Status Timeline
          {modificationLogs?.length === 0 && (
            <span className="text-sm text-gray-400 font-medium ml-2">
              (No Data)
            </span>
          )}
        </h2>
      </div>

      <div className="bg-gray-100 rounded-lg border border-gray-200 px-4 py-1 w-full overflow-x-auto scrollbar-hide">
        {modificationLogs?.length > 0 ? (
          <div className="relative flex items-center gap-6 min-w-max py-4">
            {modificationLogs.map((item, index) => {
              const { date, time } = formatDateTime(item.create_date);
              const { borderColor, statusBg, statusText, lineColor } =
                getIconAndColors();

              return (
                <div
                  key={index}
                  className="relative flex flex-col items-center"
                >
                  {index < modificationLogs?.length - 1 && (
                    <div
                      className={`absolute top-6 left-full w-8 h-0.5 ${lineColor} z-0`}
                      style={{ transform: "translateY(-50%)" }}
                    ></div>
                  )}

                  <div
                    className={`bg-white border ${borderColor} rounded-lg shadow-sm hover:shadow-md transition-all duration-200 w-48`}
                  >
                    <div className="px-3 py-2">
                      <div className="flex justify-center mb-2">
                        <span
                          className={`${statusBg} ${statusText} text-xs font-medium px-2.5 py-1 rounded-full capitalize`}
                        >
                          {item.status || "Updated"}
                        </span>
                      </div>

                      <div className="flex justify-center gap-2 text-xs text-gray-600 mb-2">
                        <span className="font-medium">{date}</span>
                        <span>•</span>
                        <span>{time}</span>
                      </div>

                      <div className="flex flex-col items-center justify-center gap-1">
                        <User className="w-3 h-3 text-gray-500" />
                        <span className="text-xs font-medium text-gray-700">
                          {item.userid || "System"}
                        </span>

                        {item.Comments && (
                          <span className="text-[11px] text-gray-500 text-center mt-1">
                            “{item.Comments}”
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-12 text-center h-full">
            <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mb-3">
              <Clock className="w-6 h-6 text-gray-400" />
            </div>
            <p className="text-gray-500 text-lg font-medium mb-1">
              No Timeline Data
            </p>
            <p className="text-gray-400 text-sm">
              No history available for this document
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TimelineTab;
